import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';

import {
  getSecondaryLabel,
  buildRangeKey,
  formatRangeLabel,
  isNonAidRange,
  isNonAgiRange,
  isPellRange,
} from '../definition-tab.constants';
import {
  COMPARISON_METRICS,
  MAX_COLUMNS_WITHOUT_SCROLL,
  RANK_COLUMN_STYLE,
  getColumnSizing,
} from '../regime-comparison.constants';
import useAutofill from '../hooks/use-autofill';
import { useRegimeComparison } from '../hooks/use-regime-comparison';

import MatrixCell from './matrix-cell';
import AutofillPopover from './autofill-popover';
import ComparisonAccordion from './comparison-accordion';
import RegimeComparisonGrid from './regime-comparison-grid';
import { FLAT_AMOUNT } from '../../../../aid-studio.constants';

type UnitField = 'amount' | 'discount';

interface CalculationRange {
  from: number | null;
  to: number | null;
  nonAid?: boolean;
  nonAgi?: boolean;
}

interface CalculationCellValue {
  amount: number;
  discount: number;
}

interface AwardCalculationTabContentProps {
  ranges: CalculationRange[];
  calculatedBy: string;
  rankIds: Array<string | number>;
  rankIdToDisplayNumber: Map<string | number, string | number>;
  getCell: (rankId: string | number, rangeKey: string) => CalculationCellValue;
  updateCell: (
    rankId: string | number,
    rangeKey: string,
    field: UnitField,
    value: number
  ) => void;
  regimeId: number | null;
  readOnly?: boolean;
}

const AwardCalculationTabContent = ({
  ranges,
  calculatedBy,
  rankIds,
  rankIdToDisplayNumber,
  getCell,
  updateCell,
  regimeId,
  readOnly,
}: AwardCalculationTabContentProps): React.JSX.Element => {
  const secondaryLabel = getSecondaryLabel(calculatedBy);
  const isFlatAmount = calculatedBy === FLAT_AMOUNT;
  const fundsUsingLabel = secondaryLabel === '%' ? 'Need %' : 'Flat $';
  const anchorCellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // A Pell regime's Non-Aid amount duplicates its base sibling 1:1, so it
  // carries no information the Pell column doesn't already imply — hide it
  // whenever a Pell column is present, regardless of how the underlying
  // calculation ended up with a Non-Aid cell.
  const hasPellColumn = ranges.some(isPellRange);
  const displayRanges = hasPellColumn
    ? ranges.filter((range) => !isNonAidRange(range))
    : ranges;

  const rangeKeys = useMemo(
    () => displayRanges.map((r) => buildRangeKey(r)),
    [displayRanges]
  );

  const rangeKeyToRange = useMemo(() => {
    const map = new Map<string, CalculationRange>();

    displayRanges.forEach((r) => map.set(buildRangeKey(r), r));

    return map;
  }, [displayRanges]);

  const isDiscountReadOnly = useCallback(
    (rangeKey: string): boolean => {
      const range = rangeKeyToRange.get(rangeKey);

      return range ? isNonAidRange(range) : false;
    },
    [rangeKeyToRange]
  );

  const {
    suggestion,
    handleCellChange,
    handleCellFocus,
    handleCellBlur,
    cancelBlur,
    applySuggestion,
    dismissSuggestion,
    isCellFocused,
  } = useAutofill({
    rankIds,
    rangeKeys,
    getCell,
    updateCell,
    isDiscountReadOnly,
  });

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Tab' || e.shiftKey || !suggestion || !popoverRef.current)
        return;

      const firstButton =
        popoverRef.current.querySelector<HTMLButtonElement>('button');

      if (!firstButton) return;

      e.preventDefault();
      firstButton.focus();
      cancelBlur();
    },
    [suggestion, cancelBlur]
  );

  const {
    isLoading: isComparisonLoading,
    isRefreshing: isComparisonRefreshing,
    isReady: isComparisonReady,
    columns: comparisonColumns,
  } = useRegimeComparison(regimeId, displayRanges);

  if (!displayRanges.length) {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          color: 'tokens.gray.500',
        }}
        data-testid="award-calculation-tab-content"
      >
        Define ranges in the Definition tab to configure award calculations.
      </Stack>
    );
  }

  const isFixedColumnWidth =
    hasPellColumn || displayRanges.length + 1 > MAX_COLUMNS_WITHOUT_SCROLL;
  const column = getColumnSizing(isFixedColumnWidth);

  return (
    <Stack
      sx={{
        position: 'relative',
        gap: 1,
        p: 1.5,
        minWidth: 0,
        maxWidth: '100%',
      }}
      data-testid="award-calculation-tab-content"
    >
      <div className="w-full max-w-full overflow-x-auto scrollbar-thin">
        <div
          className={classNames(
            'flex flex-col gap-3',
            isFixedColumnWidth ? 'w-max min-w-full' : 'w-full'
          )}
        >
          <ComparisonAccordion
            title={`Award: funds using ${fundsUsingLabel}`}
            defaultExpanded
            testId="award-calculation-accordion"
          >
            <div
              className={classNames(
                'border border-navy-100 rounded-md overflow-hidden',
                isFixedColumnWidth ? 'w-max' : 'w-full'
              )}
              inert={readOnly}
            >
              <Stack direction="row">
                <Stack
                  direction="row"
                  sx={{
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                    borderBottomWidth: '1px',
                    borderColor: 'tokens.navy.100',
                  }}
                  style={RANK_COLUMN_STYLE}
                >
                  <span className="text-xs font-semibold text-gray-500 tracking-[1.6px] leading-4">
                    Rank
                  </span>
                </Stack>

                <Stack
                  sx={{
                    flex: '1 1 0%',
                    minWidth: 0,
                    borderLeftWidth: '1px',
                    borderColor: 'tokens.navy.100',
                  }}
                >
                  <Stack direction="row">
                    {displayRanges.map((range, index) => (
                      <div
                        key={buildRangeKey(range)}
                        className={classNames(
                          'flex items-center justify-center pt-2 pb-1.5 px-2',
                          column.className,
                          { 'border-l border-navy-100': index > 0 }
                        )}
                        style={column.style}
                      >
                        <span
                          className={classNames(
                            'text-xs font-semibold tracking-[1.6px] leading-4 text-center truncate',
                            isNonAidRange(range) || isNonAgiRange(range)
                              ? 'text-gray-700'
                              : 'text-navy-800'
                          )}
                        >
                          {formatRangeLabel(range)}
                        </span>
                      </div>
                    ))}
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{
                      borderBottomWidth: '1px',
                      borderColor: 'tokens.navy.100',
                    }}
                  >
                    {displayRanges.map((range, index) => (
                      <div
                        key={buildRangeKey(range)}
                        className={classNames(
                          'flex items-center justify-between py-0.5 text-xs font-semibold text-gray-500 leading-4',
                          column.className,
                          { 'border-l border-navy-100': index > 0 }
                        )}
                        style={column.style}
                      >
                        {isPellRange(range) ? (
                          <span className="flex-1 text-center uppercase">
                            {fundsUsingLabel}
                          </span>
                        ) : (
                          <>
                            <span className="flex-1 text-center">
                              {isFlatAmount ? 'MERIT' : '$'}
                            </span>
                            <span className="flex-1 text-center">
                              {isFlatAmount ? 'GRANT' : secondaryLabel}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </Stack>
                </Stack>
              </Stack>

              {rankIds.map((rankId, rankIndex) => {
                const isLastRow = rankIndex === rankIds.length - 1;
                const displayRank = rankIdToDisplayNumber.get(rankId) ?? rankId;

                return (
                  <Stack
                    direction="row"
                    key={rankId}
                    data-testid={`matrix-row-${rankId}`}
                  >
                    <Stack
                      direction="row"
                      sx={[
                        {
                          flexShrink: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 1,
                        },
                        !isLastRow && {
                          borderBottomWidth: '1px',
                          borderColor: 'tokens.navy.100',
                        },
                      ]}
                      style={RANK_COLUMN_STYLE}
                    >
                      <span className="text-xs font-semibold text-gray-700 leading-4">
                        {displayRank}
                      </span>
                    </Stack>

                    <Stack
                      direction="row"
                      sx={[
                        {
                          flex: '1 1 0%',
                          minWidth: 0,
                          borderLeftWidth: '1px',
                          borderColor: 'tokens.navy.100',
                        },
                        !isLastRow && { borderBottomWidth: '1px' },
                      ]}
                    >
                      {displayRanges.map((range, colIndex) => {
                        const rangeKey = buildRangeKey(range);
                        const cell = getCell(rankId, rangeKey);
                        const nonAid = isNonAidRange(range);
                        const pell = isPellRange(range);
                        const amountFocused = isCellFocused(
                          rankId,
                          rangeKey,
                          'amount'
                        );
                        const discountFocused = isCellFocused(
                          rankId,
                          rangeKey,
                          'discount'
                        );

                        return (
                          <div
                            key={rangeKey}
                            className={classNames(
                              'flex items-center',
                              column.className,
                              { 'border-l border-navy-100': colIndex > 0 }
                            )}
                            style={column.style}
                            data-testid={`matrix-cell-${rankId}-${rangeKey}`}
                          >
                            {!pell && (
                              <MatrixCell
                                value={cell.amount}
                                prefix="$"
                                onChange={(val) =>
                                  handleCellChange(
                                    rankId,
                                    rangeKey,
                                    'amount',
                                    val
                                  )
                                }
                                onFocus={() =>
                                  handleCellFocus(rankId, rangeKey, 'amount')
                                }
                                onBlur={handleCellBlur}
                                onKeyDown={
                                  amountFocused ? handleInputKeyDown : undefined
                                }
                                isFocused={amountFocused}
                                cellRef={
                                  amountFocused ? anchorCellRef : undefined
                                }
                                data-testid={`matrix-input-amount-${rankId}-${rangeKey}`}
                                aria-label={`Amount for rank ${displayRank}, range ${formatRangeLabel(
                                  range
                                )}`}
                              />
                            )}
                            <MatrixCell
                              value={cell.discount}
                              prefix={isFlatAmount ? secondaryLabel : undefined}
                              suffix={isFlatAmount ? undefined : secondaryLabel}
                              readOnly={nonAid}
                              onChange={(val) =>
                                handleCellChange(
                                  rankId,
                                  rangeKey,
                                  'discount',
                                  val
                                )
                              }
                              onFocus={() =>
                                handleCellFocus(rankId, rangeKey, 'discount')
                              }
                              onBlur={handleCellBlur}
                              onKeyDown={
                                discountFocused ? handleInputKeyDown : undefined
                              }
                              isFocused={discountFocused}
                              cellRef={
                                discountFocused ? anchorCellRef : undefined
                              }
                              data-testid={`matrix-input-discount-${rankId}-${rangeKey}`}
                              aria-label={`Discount for rank ${displayRank}, range ${formatRangeLabel(
                                range
                              )}`}
                            />
                          </div>
                        );
                      })}
                    </Stack>
                  </Stack>
                );
              })}
            </div>
          </ComparisonAccordion>

          {isComparisonLoading && (
            <Box
              sx={{ position: 'relative', height: '6rem' }}
              data-testid="regime-comparison-loader"
            >
              <Loader
                loading
                overlay="white"
                backdrop="white"
                fullScreen={false}
              />
            </Box>
          )}

          {isComparisonReady && (
            <Stack
              sx={{ position: 'relative', gap: 1.5 }}
              data-testid="regime-comparison-metrics"
            >
              {COMPARISON_METRICS.map((metric) => (
                <ComparisonAccordion
                  key={metric.key}
                  title={metric.label}
                  testId={`comparison-accordion-${metric.key}`}
                >
                  <RegimeComparisonGrid
                    columns={comparisonColumns}
                    metric={metric}
                    isFixedColumnWidth={isFixedColumnWidth}
                  />
                </ComparisonAccordion>
              ))}
              <Loader
                loading={isComparisonRefreshing}
                overlay="white"
                backdrop="white"
                fullScreen={false}
              />
            </Stack>
          )}
        </div>
      </div>

      {suggestion && (
        <AutofillPopover
          value={suggestion.value}
          direction={suggestion.direction}
          unitType={suggestion.unitType}
          secondaryLabel={secondaryLabel}
          isRestrictedToRemainder={Boolean(suggestion.restrictAfter)}
          anchorRef={anchorCellRef}
          popoverRef={popoverRef}
          onApply={applySuggestion}
          onDismiss={dismissSuggestion}
          cancelBlur={cancelBlur}
        />
      )}
    </Stack>
  );
};

export default AwardCalculationTabContent;
